import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SubHeader from "../../components/SubHeader/SubHeader";
import InputField from "../../components/InputField/InputField";
import Loader from "../../components/Loader/Loader";
import ButtonComponent from "../../components/ButtonComponent/ButtonComponent";
import { errorToast, successToast } from "../../services/ToastHelper";
import { postApi } from "../../utils/apiService";
import {
    ADD_BRANCH,
    UPDATE_BRANCH,
    GET_BRANCH_BY_ID
} from "../../utils/apiPath";
import "./ManageBranch.scss";

const AddEditBranch = () => {
    const { branchId } = useParams();
    const navigate = useNavigate();
    const mode = branchId ? "edit" : "add";

    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        name: "",
        code: "",
        address: "",
        city: "",
        state: "",
        country: "India",
        pincode: "",
        emailId: "",
        phoneNumber: "",
        principalName: "",
        branchAdmin: {
            userId: 0,
            userName: "",
            emailId: "",
            phoneNumber: ""
        }
    });
    const [errors, setErrors] = useState({});

    const fetchBranchDetails = async () => {
        setIsLoading(true);
        const { statusCode, data, message } = await postApi(GET_BRANCH_BY_ID, { branchId });
        if (statusCode === 200) {
            setFormData({
                name: data.name || "",
                code: data.code || "",
                address: data.address || "",
                city: data.city || "",
                state: data.state || "",
                country: data.country || "India",
                pincode: data.pincode || "",
                emailId: data.emailId || "",
                phoneNumber: data.phoneNumber || "",
                principalName: data.principalName || "",
                branchAdmin: {
                    userId: data.branchAdmin?.userId || 0,
                    userName: data.branchAdmin?.userName || "",
                    emailId: data.branchAdmin?.emailId || "",
                    phoneNumber: data.branchAdmin?.phoneNumber || ""
                }
            });
        } else {
            errorToast(message);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        if (mode === "edit") {
            fetchBranchDetails();
        }
    }, [branchId]);

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Branch name is required";
        if (!formData.code.trim()) newErrors.code = "Branch code is required";
        if (!formData.emailId.trim()) newErrors.emailId = "Email is required";
        if (!formData.phoneNumber.trim()) newErrors.phoneNumber = "Phone number is required";
        if (!formData.address.trim()) newErrors.address = "Address is required";
        if (!formData.city.trim()) newErrors.city = "City is required";
        
        // Admin validation
        if (!formData.branchAdmin.userName.trim()) newErrors.adminName = "Admin name is required";
        if (!formData.branchAdmin.emailId.trim()) newErrors.adminEmail = "Admin email is required";
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setIsSubmitting(true);
        const payload = { ...formData };
        if (mode === "edit") {
            payload.branchId = branchId;
        }

        const API_URL = mode === "add" ? ADD_BRANCH : UPDATE_BRANCH;
        const { statusCode, message } = await postApi(API_URL, payload);

        if (statusCode === 200) {
            successToast(message);
            navigate("/manage-branch");
        } else {
            errorToast(message);
        }
        setIsSubmitting(false);
    };

    const handleAdminChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            branchAdmin: {
                ...prev.branchAdmin,
                [field]: value
            }
        }));
    };

    return (
        <div className="manage-branch">
            {isLoading && <Loader />}
            <SubHeader
                title={mode === "add" ? "Add New Branch" : "Edit Branch"}
                showBack={true}
                onBack={() => navigate("/manage-branch")}
            />

            <div className="form-container-card">
                <div className="branch-form">
                    <h4 className="section-title">Branch Information</h4>
                    <div className="inner-row">
                        <InputField
                            title="Branch Name"
                            placeholder="Enter branch name"
                            value={formData.name}
                            required
                            errorText={errors.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        />
                        <InputField
                            title="Branch Code"
                            placeholder="Enter branch code"
                            value={formData.code}
                            required
                            errorText={errors.code}
                            onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                        />
                    </div>
                    <div className="inner-row">
                        <InputField
                            title="Email ID"
                            placeholder="Enter branch email"
                            value={formData.emailId}
                            required
                            errorText={errors.emailId}
                            onChange={(e) => setFormData(prev => ({ ...prev, emailId: e.target.value }))}
                        />
                        <InputField
                            title="Phone Number"
                            placeholder="Enter phone number"
                            value={formData.phoneNumber}
                            required
                            errorText={errors.phoneNumber}
                            onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                        />
                    </div>
                    <div className="inner-row">
                        <InputField
                            title="Principal Name"
                            placeholder="Enter principal name"
                            value={formData.principalName}
                            onChange={(e) => setFormData(prev => ({ ...prev, principalName: e.target.value }))}
                        />
                        <InputField
                            title="Address"
                            placeholder="Enter full address"
                            value={formData.address}
                            required
                            errorText={errors.address}
                            onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                        />
                    </div>
                    <div className="inner-row">
                        <InputField
                            title="City"
                            placeholder="Enter city"
                            value={formData.city}
                            required
                            errorText={errors.city}
                            onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                        />
                        <InputField
                            title="State"
                            placeholder="Enter state"
                            value={formData.state}
                            onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                        />
                    </div>
                    <div className="inner-row">
                        <InputField
                            title="Pincode"
                            placeholder="Enter pincode"
                            value={formData.pincode}
                            onChange={(e) => setFormData(prev => ({ ...prev, pincode: e.target.value }))}
                        />
                        <InputField
                            title="Country"
                            placeholder="Enter country"
                            value={formData.country}
                            onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                        />
                    </div>

                    <h4 className="section-title mt-24">Branch Admin Details</h4>
                    <div className="inner-row">
                        <InputField
                            title="Admin User Name"
                            placeholder="Enter admin name"
                            value={formData.branchAdmin.userName}
                            required
                            errorText={errors.adminName}
                            onChange={(e) => handleAdminChange("userName", e.target.value)}
                        />
                        <InputField
                            title="Admin Email ID"
                            placeholder="Enter admin email"
                            value={formData.branchAdmin.emailId}
                            required
                            errorText={errors.adminEmail}
                            onChange={(e) => handleAdminChange("emailId", e.target.value)}
                        />
                    </div>
                    <div className="inner-row">
                        <InputField
                            title="Admin Phone Number"
                            placeholder="Enter admin phone"
                            value={formData.branchAdmin.phoneNumber}
                            onChange={(e) => handleAdminChange("phoneNumber", e.target.value)}
                        />
                        <div />
                    </div>

                    <div className="form-actions">
                        <ButtonComponent variant="danger" onClick={() => navigate("/manage-branch")}>
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent variant="primary" onClick={handleSubmit} loading={isSubmitting}>
                            {mode === "add" ? "Submit" : "Update"}
                        </ButtonComponent>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddEditBranch;
